import logging from 'utils/logging';
import _ from 'lodash';
import {
  requestNarration,
  playNarration,
  interruptNarration,
  HOST_STATUS,
  narrationReady
} from './hostStateSlice';
import { getTriggeredMoments, getPlaybackMoments } from './moments/index';
import { registerCacheReset } from './momentCacheManager';


const log = logging.getLogger('hostOrchestrator');
log.setLevel('info');

// The orchestrator manages general-purpose state tracking that applies
// across all moment types. Specific state for individual moment types
// should be kept in their respective moment files.

// Track which narrations have been recently triggered to prevent duplicates
const recentlyTriggeredMoments = new Set();

// Track which narrations have been played to avoid repeats
const playedNarrations = new Set();

export const resetOrchestratorCaches = () => {
  recentlyTriggeredMoments.clear();
  playedNarrations.clear();
};
registerCacheReset('round', resetOrchestratorCaches);

/**
 * Checks if any playback moment wants to interrupt the current narration
 * 
 * @param {Object} params - The parameters for interruption check
 * @param {Object} params.gameState - Current game state
 * @param {Object} params.hostState - Current host state  
 * @param {Array} params.players - Array of player objects
 * @param {Array} params.narrations - Array of narration objects
 * @param {Set} params.playedNarrations - Set of played narration keys
 * @returns {Object|null} - The moment that wants to interrupt, or null
 */
const shouldInterruptCurrentNarration = ({ gameState, hostState, players, narrations, playedNarrations }) => {
  // Only check for interruption if host is currently speaking
  if (hostState.status !== HOST_STATUS.SPEAKING) {
    return null;
  }
  
  // Get all playback moments and check if any want to interrupt
  const playbackMoments = getPlaybackMoments(gameState, hostState, narrations, playedNarrations, players);

  log.debug("Playback moments for interrupting: ", playbackMoments);

  for (const moment of playbackMoments) {
    if (moment.shouldInterrupt && moment.shouldInterrupt({ gameState, hostState, players, narrations })) {
      log.info(`Playback moment ${moment.id} wants to interrupt current narration`);
      return moment;
    }
  }
  
  return null;
};

 
/**
 * Handles narration generation based on game state changes
 * Uses the moments-based approach to detect when narration should be generated
 * 
 * @param {Object} params - State change parameters
 * @param {string} params.gameId - Game ID
 * @param {Object} params.prevGameState - Previous game state
 * @param {Object} params.currentGameState - Current game state
 * @param {Object} params.currentHostState - Current host state
 * @param {Array} params.players - Player array
 * @param {Function} params.dispatch - Redux dispatch function
 */
export const handleTriggeredMoments = async (params) => {
  const { 
    gameId, 
    prevGameState, 
    currentGameState, 
    currentHostState,
    players, 
    dispatch,
    narrations,
    questions,
    submissions,
    gameObject
  } = params;
  
  // Don't proceed if missing critical data
  if (!gameId || !currentGameState) {
    log.warn('Missing critical data for narration generation');
    return;
  }
  
  log.debug('handleTriggeredMoments called with:', {
    gameId,
    prevStateKey: prevGameState ? `${prevGameState.currentState}_${prevGameState.round}_${prevGameState.roundState}` : 'null',
    currentStateKey: `${currentGameState.currentState}_${currentGameState.round}_${currentGameState.roundState}`,
    hostStatus: currentHostState?.status || 'undefined'
  });

  const narrationKeys = narrations.map(narration => narration.key);

  // Get triggered moments for this state change
  const triggeredMoments = getTriggeredMoments(prevGameState, currentGameState, players, questions, narrationKeys, submissions, gameObject);
  
  if (triggeredMoments.length === 0) {
    log.debug("No moments triggered for this state change");
    return;
  }
  
  // For each triggered moment, handle narration generation and/or trigger actions
  for (const moment of triggeredMoments) {
    log.debug(`Processing triggered moment: ${moment.id}`);
    
    // Handle trigger actions if needed
    if (moment.shouldTriggerAction && moment.triggerAction) {
      log.info(`Executing trigger action for moment: ${moment.id}`);
      try {
        await moment.triggerAction(gameObject, players, dispatch);
      } catch (error) {
        log.error(`Error executing trigger action for moment ${moment.id}:`, error);
      }
    }
    
    // Handle narration generation if needed
    if (moment.shouldGenerateNarration) {
      // Support both single narrationParams and multipleNarrations array
      const narrationsList = moment.multipleNarrations
        ? moment.multipleNarrations
        : moment.narrationParams
          ? [moment.narrationParams]
          : [];

      for (const narrationParams of narrationsList) {
        const { narrationKey, momentType, context } = narrationParams;

        // Skip if this narration was recently triggered
        if (recentlyTriggeredMoments.has(narrationKey)) {
          log.debug(`Skipping duplicate trigger for narration ${narrationKey}`);
          continue;
        }

        // Mark this narration as recently triggered
        recentlyTriggeredMoments.add(narrationKey);

        log.debug(`Generating narration for moment: ${moment.id}, key: ${narrationKey}`);

        try {
          // Check if narration already exists in the narrations list
          const narration = narrations && _.find(narrations, { key: narrationKey });

          if (narration) {
            log.warn(`Narration for ${narrationKey} already exists, skipping generation`);
            await dispatch(narrationReady({ gameId, narrationKey }));
            continue;
          }

          log.info("requesting narration for moment", moment);

          await dispatch(requestNarration({
            gameId,
            narrationKey,
            momentType,
            state: currentGameState,
            context: context
          }));

          log.debug(`Successfully requested narration for ${narrationKey}`);
          await dispatch(narrationReady({ gameId, narrationKey }));
        } catch (error) {
          log.error(`Error generating narration for moment ${moment.id}, key ${narrationKey}:`, error);
        }
      }
    }
  }
};

/**
 * Handles narration playback based on game state and host state
 * Uses playbackMoments to determine when to play specific narrations
 * 
 * @param {Object} params - Playback parameters
 * @param {string} params.gameId - Game ID
 * @param {Object} params.gameState - Current game state
 * @param {Object} params.hostState - Current host state
 * @param {Function} params.dispatch - Redux dispatch function
 */
export const handlePlayNarration = async (params) => {
  const { gameId, gameState, hostState, narrations, players, dispatch } = params;
  log.debug('handlePlayNarration called with:', params);
  
  // Don't proceed if missing critical data
  if (!gameId || !gameState || !hostState) {
    log.warn('Missing critical data for narration playback');
    return;
  }
  
  // Check if current narration should be interrupted
  const interruptingMoment = shouldInterruptCurrentNarration({ 
    gameState, 
    hostState, 
    players, 
    narrations, 
    playedNarrations 
  });
  
  if (interruptingMoment) {
    log.info(`Interruption condition detected by moment ${interruptingMoment.id} - interrupting current narration`);
    
    // Get the current narration that's being interrupted
    const currentNarration = narrations.find(n => n.key === hostState.currentNarrationKey);
    
    // Interrupt the current narration
    await dispatch(interruptNarration({ 
      gameId, 
      narrationId: currentNarration?.id 
    }));
    
    // Don't return - let the normal playback logic continue
    // The interrupting moment will be picked up by getPlaybackMoments after interruption
    log.info(`Interruption initiated - continuing to check for playback moments`);
  }
  
  // Validate narrations object
  if (!narrations) {
    log.warn('Narrations data is missing or invalid');
    return;
  }

  if (_.isEmpty(narrations)) {
    log.debug('No narrations to play at this time');
    return;
  }
  
  log.debug('Host state: ', hostState);
  // If already speaking, do nothing
  if (hostState.status === HOST_STATUS.SPEAKING) {
    log.debug('Host is already speaking, not starting new narration');
    return;
  }
  
  log.debug('Checking for narrations to play...', gameState, hostState, playedNarrations);
  
  // Get playback moments that should be played in current state
  const momentsToPlay = getPlaybackMoments(gameState, hostState, narrations, playedNarrations, players);

  if (momentsToPlay.length === 0) {
    log.debug('No narrations to play at this time');
    return;
  }

  
  // Play the first moment (if any)
  if (momentsToPlay.length > 0) {
    log.info('Playing moments! Moments to play:', momentsToPlay);
    const moment = momentsToPlay[0];
    // Use the narration from the moment
    const narration = moment.narration;
    log.info(`Playing narration for moment: ${moment.id}, key: ${narration.key}`);

    // Add to played narrations to avoid repeats
    playedNarrations.add(narration.key);

    // Play the narration
    dispatch(playNarration({
      gameId,
      narration,
      onComplete: moment.onComplete
    }));
  } else {
    log.debug('No narrations to play at this time');
  }
};

/**
 * Main entry point for state change handling
 * This function coordinates both narration generation and playback
 * 
 * @param {Object} params - State change parameters
 * @returns {Promise} - A promise that resolves when state handling is complete
 */
export const handleStateChange = async (params) => {
  const { gameId, currentGameState, currentHostState, narrations } = params;
  
  // Log the current state
  log.debug(`Handling state change for game ${gameId}:`, {
    currentState: currentGameState?.currentState,
    hostStatus: currentHostState?.status
  });

  try {
    // First, handle narration generation based on state changes
    await handleTriggeredMoments(params);
    
    // Then, handle narration playback
    await handlePlayNarration({
      gameId,
      gameState: currentGameState,
      hostState: currentHostState,
      narrations,
      players: params.players,
      dispatch: params.dispatch
    });
  } catch (error) {
    log.error(`Error in handleStateChange:`, error);
  }
};
